import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Loader2,
  MoreVertical,
  Plus,
  Search,
  Square,
  SquareCheckBig,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { FieldError, FieldLabel, TextField } from '@/components/FormField';
import { SettingsCard } from '@/features/settings/SettingsCard';
import { StatusStrip } from '@/features/settings/StatusStrip';
import { ApiError } from '@/features/auth/api';
import { useCompanyLookup } from '@/features/customers/api';
import { normalizePlPostalCode } from '@/features/customers/format';
import {
  isValidEmail,
  isValidKrs,
  isValidNip,
  isValidPhone,
  isValidPlPostalCode,
  isValidRegon,
} from '@/features/customers/validation';
import {
  useIssuerProfile,
  useSaveIssuerProfile,
  type IssuerProfile,
  type IssuerProfileInput,
  type NumberingPattern,
  type VatStatus,
} from './api';

type FieldKey =
  | 'nip'
  | 'companyName'
  | 'locality'
  | 'buildingNumber'
  | 'postalCode'
  | 'postTown'
  | 'exemptionLegalBasis'
  | 'contactEmail'
  | 'contactPhone'
  | 'krs'
  | 'regon'
  | 'bankAccounts';

type FieldErrors = Partial<Record<FieldKey, string>>;

interface BankRow {
  key: number;
  account: string;
  bankName: string;
  isDefault: boolean;
}

/** Field order as rendered, so the first error the user sees is the one that gets focus. */
const FOCUS_ORDER: Array<[FieldKey, string]> = [
  ['nip', 'issuer-nip'],
  ['krs', 'issuer-krs'],
  ['regon', 'issuer-regon'],
  ['companyName', 'issuer-company-name'],
  ['locality', 'issuer-locality'],
  ['buildingNumber', 'issuer-building'],
  ['postalCode', 'issuer-postal'],
  ['postTown', 'issuer-town'],
  ['contactEmail', 'issuer-email'],
  ['contactPhone', 'issuer-phone'],
  ['exemptionLegalBasis', 'issuer-exemption'],
];

const CODE_TO_FIELD: Record<string, { field: FieldKey | null; message: string }> = {
  INVALID_NIP: { field: 'nip', message: 'issuer.form.validation.nip_invalid' },
  NIP_NOT_IN_CERTIFICATES: { field: 'nip', message: 'issuer.form.errors.nip_not_in_certificates' },
  INVALID_COMPANY_NAME: {
    field: 'companyName',
    message: 'issuer.form.validation.company_name_required',
  },
  INVALID_ADDRESS: { field: 'locality', message: 'issuer.form.validation.address_invalid' },
  UNSUPPORTED_COUNTRY: { field: null, message: 'issuer.form.errors.failed' },
  MISSING_EXEMPTION_BASIS: {
    field: 'exemptionLegalBasis',
    message: 'issuer.form.validation.exemption_required',
  },
  INVALID_CONTACT_EMAIL: { field: 'contactEmail', message: 'issuer.form.validation.email_invalid' },
  INVALID_CONTACT_PHONE: { field: 'contactPhone', message: 'issuer.form.validation.phone_invalid' },
  INVALID_KRS: { field: 'krs', message: 'issuer.form.validation.krs_invalid' },
  INVALID_REGON: { field: 'regon', message: 'issuer.form.validation.regon_invalid' },
  INVALID_BANK_ACCOUNT: {
    field: 'bankAccounts',
    message: 'issuer.form.errors.invalid_bank_account',
  },
  DUPLICATE_BANK_ACCOUNT: {
    field: 'bankAccounts',
    message: 'issuer.form.errors.duplicate_bank_account',
  },
  INVALID_BANK_ACCOUNT_DEFAULT: {
    field: 'bankAccounts',
    message: 'issuer.form.errors.bank_account_default',
  },
};

function focusFirstError(errors: FieldErrors): void {
  for (const [key, id] of FOCUS_ORDER) {
    if (errors[key]) {
      document.getElementById(id)?.focus();
      return;
    }
  }
}

export function IssuerProfileSection({ className }: { className?: string }) {
  const { t } = useTranslation();
  const query = useIssuerProfile();
  const envelope = query.data;
  const profile = envelope?.profile ?? null;
  const loaded = !query.isPending && !query.isError && envelope != null;
  const [editing, setEditing] = useState(false);

  return (
    <SettingsCard
      icon={Building2}
      title={t('issuer.title')}
      description={t('issuer.description')}
      className={className}
      action={
        loaded ? (
          profile ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  aria-label={t('issuer.manage')}
                >
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditing(true)}>
                  {t('issuer.edit')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button type="button" onClick={() => setEditing(true)}>
              <Plus data-icon="inline-start" aria-hidden />
              {t('issuer.set_up')}
            </Button>
          )
        ) : undefined
      }
    >
      {query.isPending ? (
        <StatusStrip>
          <span className="text-sm text-muted-foreground">{t('issuer.loading')}</span>
          <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />
        </StatusStrip>
      ) : query.isError || !envelope ? (
        <StatusStrip>
          <span className="text-sm text-destructive">{t('issuer.load_error')}</span>
        </StatusStrip>
      ) : (
        <div className="space-y-3">
          {!envelope.readiness.canIssue ? (
            <StatusStrip>
              <span className="text-sm text-muted-foreground">
                {t(`issuer.readiness.blocker.${envelope.readiness.blockers[0] ?? 'NO_PROFILE'}`)}
              </span>
            </StatusStrip>
          ) : null}
          {profile ? <ProfileSummary profile={profile} /> : null}
        </div>
      )}
      {loaded ? (
        <IssuerProfileDialog profile={profile} open={editing} onOpenChange={setEditing} />
      ) : null}
    </SettingsCard>
  );
}

function IssuerProfileDialog({
  profile,
  open,
  onOpenChange,
}: {
  profile: IssuerProfile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>
            {profile ? t('issuer.form.edit_title') : t('issuer.form.create_title')}
          </DialogTitle>
          <DialogDescription>{t('issuer.description')}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="cn-dialog-scroll">
          <div className="px-6 pb-6">
            <IssuerProfileForm profile={profile} onDone={() => onOpenChange(false)} />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function IssuerProfileForm({
  profile,
  onDone,
}: {
  profile: IssuerProfile | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();

  const [nip, setNip] = useState(profile?.nip ?? '');
  const [companyName, setCompanyName] = useState(profile?.companyName ?? '');
  const [locality, setLocality] = useState(profile?.address.locality ?? '');
  const [street, setStreet] = useState(profile?.address.street ?? '');
  const [buildingNumber, setBuildingNumber] = useState(profile?.address.buildingNumber ?? '');
  const [unitNumber, setUnitNumber] = useState(profile?.address.unitNumber ?? '');
  const [postalCode, setPostalCode] = useState(profile?.address.postalCode ?? '');
  const [postTown, setPostTown] = useState(profile?.address.postTown ?? '');
  const [vatStatus, setVatStatus] = useState<VatStatus>(profile?.vatStatus ?? 'ACTIVE');
  const [exemptionLegalBasis, setExemptionLegalBasis] = useState(
    profile?.exemptionLegalBasis ?? '',
  );
  const [numberingPattern, setNumberingPattern] = useState<NumberingPattern>(
    profile?.numberingPattern ?? 'INDEX_YEAR',
  );
  const [contactEmail, setContactEmail] = useState(profile?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(profile?.contactPhone ?? '');
  const [krs, setKrs] = useState(profile?.krs ?? '');
  const [regon, setRegon] = useState(profile?.regon ?? '');
  const [includeSellerContact, setIncludeSellerContact] = useState(
    profile?.includeSellerContact ?? true,
  );
  const [includeBuyerContact, setIncludeBuyerContact] = useState(
    profile?.includeBuyerContact ?? true,
  );
  const [includeKrs, setIncludeKrs] = useState(profile?.includeKrs ?? true);
  const [includeRegon, setIncludeRegon] = useState(profile?.includeRegon ?? true);
  const bankKey = useRef(0);
  const [bankAccounts, setBankAccounts] = useState<BankRow[]>(
    () =>
      profile?.bankAccounts.map((a) => ({
        key: bankKey.current++,
        ...a,
        bankName: a.bankName ?? '',
      })) ?? [],
  );
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<{
    field: FieldKey | null;
    message: string;
  } | null>(null);

  const lookup = useCompanyLookup();
  const save = useSaveIssuerProfile();

  const computeErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (nip.trim() === '') errors.nip = 'issuer.form.validation.nip_required';
    else if (!isValidNip(nip)) errors.nip = 'issuer.form.validation.nip_invalid';
    if (companyName.trim() === '')
      errors.companyName = 'issuer.form.validation.company_name_required';
    if (locality.trim() === '') errors.locality = 'issuer.form.validation.locality_required';
    if (buildingNumber.trim() === '')
      errors.buildingNumber = 'issuer.form.validation.building_required';
    if (postalCode.trim() === '') errors.postalCode = 'issuer.form.validation.postal_required';
    else if (!isValidPlPostalCode(postalCode))
      errors.postalCode = 'issuer.form.validation.postal_invalid';
    if (postTown.trim() === '') errors.postTown = 'issuer.form.validation.post_town_required';
    if (vatStatus === 'EXEMPT' && exemptionLegalBasis.trim() === '')
      errors.exemptionLegalBasis = 'issuer.form.validation.exemption_required';
    if (contactEmail.trim() !== '' && !isValidEmail(contactEmail))
      errors.contactEmail = 'issuer.form.validation.email_invalid';
    if (contactPhone.trim() !== '' && !isValidPhone(contactPhone))
      errors.contactPhone = 'issuer.form.validation.phone_invalid';
    if (krs.trim() !== '' && !isValidKrs(krs)) errors.krs = 'issuer.form.validation.krs_invalid';
    if (regon.trim() !== '' && !isValidRegon(regon))
      errors.regon = 'issuer.form.validation.regon_invalid';
    if (bankAccounts.some((a) => a.account.trim() === ''))
      errors.bankAccounts = 'issuer.form.validation.bank_account_required';
    return errors;
  };

  const errors: FieldErrors = showErrors ? computeErrors() : {};
  if (serverError?.field)
    errors[serverError.field] = errors[serverError.field] ?? serverError.message;
  const bankAccountsError = errors.bankAccounts;

  const addBankAccount = () =>
    setBankAccounts((rows) => [
      ...rows,
      { key: bankKey.current++, account: '', bankName: '', isDefault: rows.length === 0 },
    ]);

  const removeBankAccount = (key: number) =>
    setBankAccounts((rows) => {
      const next = rows.filter((r) => r.key !== key);
      if (next.length > 0 && !next.some((r) => r.isDefault)) {
        return next.map((r, i) => (i === 0 ? { ...r, isDefault: true } : r));
      }
      return next;
    });

  const setDefaultBankAccount = (key: number) =>
    setBankAccounts((rows) => rows.map((r) => ({ ...r, isDefault: r.key === key })));

  const patchBankAccount = (key: number, patch: Partial<BankRow>) =>
    setBankAccounts((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const runLookup = () => {
    const value = nip.trim();
    if (value === '') return;
    setServerError(null);
    lookup.mutate(value, {
      onSuccess: (company) => {
        setCompanyName(company.name);
        setNip(company.nip);
        // GUS's basic search carries REGON but not KRS — only the latter stays hand-entered.
        if (company.regon) setRegon(company.regon);
        if (company.address) {
          setLocality(company.address.locality);
          setStreet(company.address.street ?? '');
          setBuildingNumber(company.address.buildingNumber);
          setUnitNumber(company.address.unitNumber ?? '');
          setPostalCode(company.address.postalCode);
          setPostTown(company.address.postTown);
        }
      },
    });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (save.isPending) return;
    setServerError(null);
    const found = computeErrors();
    if (Object.keys(found).length > 0) {
      setShowErrors(true);
      focusFirstError(found);
      return;
    }
    setShowErrors(false);
    const input: IssuerProfileInput = {
      nip: nip.trim(),
      companyName: companyName.trim(),
      address: {
        locality: locality.trim(),
        street: blankToNull(street),
        buildingNumber: buildingNumber.trim(),
        unitNumber: blankToNull(unitNumber),
        postalCode: normalizePlPostalCode(postalCode),
        postTown: postTown.trim(),
        countryCode: 'PL',
      },
      vatStatus,
      exemptionLegalBasis: vatStatus === 'EXEMPT' ? exemptionLegalBasis.trim() : null,
      bankAccounts: bankAccounts.map((a) => ({
        account: a.account.trim(),
        bankName: blankToNull(a.bankName),
        isDefault: a.isDefault,
      })),
      numberingPattern,
      contactEmail: blankToNull(contactEmail),
      contactPhone: blankToNull(contactPhone),
      krs: blankToNull(krs),
      regon: blankToNull(regon),
      includeSellerContact,
      includeBuyerContact,
      includeKrs,
      includeRegon,
    };
    save.mutate(input, {
      onSuccess: () => {
        toast.success(t('issuer.toasts.saved'));
        onDone();
      },
      onError: (error: ApiError) => {
        const mapped = error.code ? CODE_TO_FIELD[error.code] : undefined;
        if (mapped) {
          setServerError(mapped);
          if (mapped.field) document.getElementById(fieldElementId(mapped.field))?.focus();
        } else {
          toast.error(t('issuer.form.errors.failed'));
        }
      },
    });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel htmlFor="issuer-nip" required>
          {t('issuer.form.fields.nip')}
        </FieldLabel>
        <div className="flex gap-2">
          <Input
            id="issuer-nip"
            inputMode="numeric"
            autoComplete="off"
            value={nip}
            onChange={(e) => {
              setNip(e.target.value);
              setServerError(null);
            }}
            aria-required
            aria-invalid={errors.nip ? true : undefined}
            aria-describedby={errors.nip ? 'issuer-nip-error' : undefined}
          />
          <Button
            type="button"
            variant="outline"
            className="whitespace-nowrap"
            disabled={lookup.isPending || nip.trim() === ''}
            onClick={runLookup}
          >
            {lookup.isPending ? (
              <Loader2 data-icon="inline-start" className="animate-spin" />
            ) : (
              <Search data-icon="inline-start" />
            )}
            {t('issuer.form.lookup')}
          </Button>
        </div>
        {errors.nip ? (
          <FieldError id="issuer-nip-error" message={t(errors.nip)} />
        ) : lookup.isError ? (
          <p className="text-xs text-destructive">{t(lookupErrorKey(lookup.error))}</p>
        ) : (
          <p className="text-xs text-muted-foreground">{t('issuer.form.lookup_hint')}</p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          id="issuer-krs"
          label={t('issuer.form.fields.krs')}
          inputMode="numeric"
          value={krs}
          onChange={(e) => setKrs(e.target.value)}
          error={errors.krs ? t(errors.krs) : undefined}
        />
        <TextField
          id="issuer-regon"
          label={t('issuer.form.fields.regon')}
          inputMode="numeric"
          value={regon}
          onChange={(e) => setRegon(e.target.value)}
          error={errors.regon ? t(errors.regon) : undefined}
        />
      </div>

      <TextField
        id="issuer-company-name"
        label={t('issuer.form.fields.company_name')}
        required
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        error={errors.companyName ? t(errors.companyName) : undefined}
      />

      <Separator />

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          id="issuer-locality"
          className="sm:col-span-2"
          label={t('issuer.form.fields.locality')}
          required
          value={locality}
          onChange={(e) => setLocality(e.target.value)}
          error={errors.locality ? t(errors.locality) : undefined}
        />
        <TextField
          id="issuer-street"
          className="sm:col-span-2"
          label={t('issuer.form.fields.street')}
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />
        <TextField
          id="issuer-building"
          label={t('issuer.form.fields.building_number')}
          required
          value={buildingNumber}
          onChange={(e) => setBuildingNumber(e.target.value)}
          error={errors.buildingNumber ? t(errors.buildingNumber) : undefined}
        />
        <TextField
          id="issuer-unit"
          label={t('issuer.form.fields.unit_number')}
          value={unitNumber}
          onChange={(e) => setUnitNumber(e.target.value)}
        />
        <TextField
          id="issuer-postal"
          label={t('issuer.form.fields.postal_code')}
          required
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          error={errors.postalCode ? t(errors.postalCode) : undefined}
        />
        <TextField
          id="issuer-town"
          label={t('issuer.form.fields.post_town')}
          required
          value={postTown}
          onChange={(e) => setPostTown(e.target.value)}
          error={errors.postTown ? t(errors.postTown) : undefined}
        />
        <TextField
          id="issuer-email"
          label={t('issuer.form.fields.contact_email')}
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          error={errors.contactEmail ? t(errors.contactEmail) : undefined}
        />
        <TextField
          id="issuer-phone"
          label={t('issuer.form.fields.contact_phone')}
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          error={errors.contactPhone ? t(errors.contactPhone) : undefined}
        />
      </div>

      <Separator />

      <ChoiceGroup
        label={t('issuer.form.fields.vat_status')}
        value={vatStatus}
        onChange={setVatStatus}
        options={[
          { value: 'ACTIVE', label: t('issuer.vat_status.ACTIVE') },
          { value: 'EXEMPT', label: t('issuer.vat_status.EXEMPT') },
        ]}
      />
      {vatStatus === 'EXEMPT' ? (
        <TextField
          id="issuer-exemption"
          label={t('issuer.form.fields.exemption_basis')}
          required
          value={exemptionLegalBasis}
          onChange={(e) => setExemptionLegalBasis(e.target.value)}
          error={errors.exemptionLegalBasis ? t(errors.exemptionLegalBasis) : undefined}
        />
      ) : null}

      <ChoiceGroup
        label={t('issuer.form.fields.numbering_pattern')}
        hint={t('issuer.form.numbering_hint')}
        value={numberingPattern}
        onChange={setNumberingPattern}
        options={[
          {
            value: 'INDEX_YEAR',
            label: t('issuer.numbering.INDEX_YEAR'),
            example: t('issuer.numbering.INDEX_YEAR_example'),
          },
          {
            value: 'INDEX_MONTH_YEAR',
            label: t('issuer.numbering.INDEX_MONTH_YEAR'),
            example: t('issuer.numbering.INDEX_MONTH_YEAR_example'),
          },
        ]}
      />

      <Separator />

      <div className="space-y-2">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">{t('issuer.form.fields.bank_accounts')}</p>
          <p className="text-xs text-muted-foreground">{t('issuer.form.bank_accounts_hint')}</p>
        </div>
        {bankAccounts.map((row) => (
          <div key={row.key} className="space-y-2 rounded-md border p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                aria-label={t('issuer.form.fields.bank_account_number')}
                placeholder={t('issuer.form.fields.bank_account_number')}
                autoComplete="off"
                value={row.account}
                onChange={(e) => patchBankAccount(row.key, { account: e.target.value })}
              />
              <Input
                aria-label={t('issuer.form.fields.bank_name')}
                placeholder={t('issuer.form.fields.bank_name')}
                autoComplete="off"
                value={row.bankName}
                onChange={(e) => patchBankAccount(row.key, { bankName: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                role="radio"
                aria-checked={row.isDefault}
                aria-label={t('issuer.form.set_default')}
                onClick={() => setDefaultBankAccount(row.key)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium outline-none transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-ring/60',
                  row.isDefault ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <span
                  className={cn(
                    'grid size-4 place-items-center rounded-full border',
                    row.isDefault ? 'border-primary' : 'border-muted-foreground/40',
                  )}
                  aria-hidden
                >
                  {row.isDefault ? <span className="size-2 rounded-full bg-primary" /> : null}
                </span>
                {t('issuer.form.default_account')}
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('issuer.form.remove_account')}
                onClick={() => removeBankAccount(row.key)}
              >
                <Trash2 />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={addBankAccount}
        >
          <Plus data-icon="inline-start" aria-hidden />
          {t('issuer.form.add_account')}
        </Button>
        <FieldError
          id="issuer-bank-accounts-error"
          message={bankAccountsError ? t(bankAccountsError) : undefined}
        />
      </div>

      <Separator />

      <div className="space-y-2">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">{t('issuer.form.fields.disclosure')}</p>
          <p className="text-xs text-muted-foreground">{t('issuer.form.disclosure_hint')}</p>
        </div>
        <ToggleRow
          label={t('issuer.form.disclosure.seller_contact')}
          checked={includeSellerContact}
          onChange={setIncludeSellerContact}
        />
        <ToggleRow
          label={t('issuer.form.disclosure.buyer_contact')}
          hint={t('issuer.form.disclosure.buyer_contact_hint')}
          checked={includeBuyerContact}
          onChange={setIncludeBuyerContact}
        />
        <ToggleRow
          label={t('issuer.form.disclosure.krs')}
          checked={includeKrs}
          onChange={setIncludeKrs}
        />
        <ToggleRow
          label={t('issuer.form.disclosure.regon')}
          checked={includeRegon}
          onChange={setIncludeRegon}
        />
      </div>

      <Button type="submit" className="w-full" disabled={save.isPending}>
        {save.isPending ? <Loader2 className="animate-spin" /> : null}
        {t('issuer.form.save')}
      </Button>
    </form>
  );
}

function ProfileSummary({ profile }: { profile: IssuerProfile }) {
  const { t } = useTranslation();
  const defaultAccount =
    profile.bankAccounts.find((a) => a.isDefault) ?? profile.bankAccounts[0] ?? null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
      <SummaryRow label={t('issuer.form.fields.company_name')} value={profile.companyName} />
      <SummaryRow label="NIP" value={profile.nip} />
      {profile.krs ? <SummaryRow label="KRS" value={profile.krs} /> : null}
      {profile.regon ? <SummaryRow label="REGON" value={profile.regon} /> : null}
      <SummaryRow label={t('issuer.summary.address')} value={profile.addressLines.join('\n')} />
      <SummaryRow
        label={t('issuer.form.fields.vat_status')}
        value={t(`issuer.vat_status.${profile.vatStatus}`)}
      />
      {defaultAccount ? (
        <SummaryRow label={t('issuer.summary.default_account')} value={defaultAccount.account} />
      ) : null}
    </dl>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium break-words whitespace-pre-line">{value}</dd>
    </>
  );
}

interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  example?: string;
}

function ChoiceGroup<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: T;
  options: ReadonlyArray<ChoiceOption<T>>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{label}</p>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-2">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.value)}
              className={cn(
                'flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2.5 text-left text-sm font-medium outline-none transition-colors',
                'focus-visible:ring-2 focus-visible:ring-ring/60',
                active
                  ? 'border-primary/40 bg-accent text-foreground ring-1 ring-primary/30'
                  : 'border-border text-muted-foreground hover:border-foreground/20 hover:bg-accent/50 hover:text-foreground',
              )}
            >
              <span>{opt.label}</span>
              {opt.example ? (
                <span className="text-xs font-normal text-muted-foreground">{opt.example}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring/60',
        checked
          ? 'border-primary/40 bg-accent/50'
          : 'border-border text-muted-foreground hover:bg-accent/30',
      )}
    >
      {checked ? (
        <SquareCheckBig className="mt-0.5 size-4 shrink-0 text-primary" />
      ) : (
        <Square className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      )}
      <span className="space-y-0.5">
        <span className="block">{label}</span>
        {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
      </span>
    </button>
  );
}

function fieldElementId(field: FieldKey): string {
  const match = FOCUS_ORDER.find(([key]) => key === field);
  return match ? match[1] : '';
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function lookupErrorKey(error: ApiError): string {
  switch (error.status) {
    case 400:
      return 'issuer.form.lookup_errors.invalid_nip';
    case 404:
      return 'issuer.form.lookup_errors.not_found';
    case 429:
      return 'issuer.form.lookup_errors.rate_limited';
    default:
      return 'issuer.form.lookup_errors.failed';
  }
}
